import { inject, Injectable } from '@angular/core';
import { NetworkMigrationResource } from '@app/shared/resources/network-migration.resource';
import { Observable } from 'rxjs';
import { NetworkMigrationWorkflow, NetworkMigrationWorkflows } from '@app/shared/models/network-migration.model';
import { map } from 'rxjs/operators';
import { BLEKeyDeliveryProgressApiResponse, BLEKeyDeliveryRetryApiResponse } from '@app/shared/models/nms.models';

@Injectable({
  providedIn: 'root'
})
export class NetworkMigrationService {
  private readonly networkMigrationResource = inject(NetworkMigrationResource);

  getManagingCompanies(): Observable<{ id: number; companyName: string }[]> {
    return this.networkMigrationResource.getManagingCompanies();
  }

  loadBLEKeyDelivery(buildingId: number): Observable<BLEKeyDeliveryProgressApiResponse> {
    return this.networkMigrationResource.loadBLEKeyDelivery(buildingId)
      .pipe(map((response) => this.normalizeBLEKeyDeliveryResponse(response)));
  }

  retryBLEKeyDelivery(buildingId: number, workflowId: number): Observable<BLEKeyDeliveryRetryApiResponse> {
    return this.networkMigrationResource.retryBLEKeyDelivery(buildingId, workflowId);
  }

  getAllMigrationTransfer(): Observable<NetworkMigrationWorkflows> {
    return this.networkMigrationResource.getAllMigrationTransfer();
  }

  startNewTransfer(buildingId: number): Observable<NetworkMigrationWorkflow> {
    return this.networkMigrationResource.startNewTransfer(buildingId);
  }

  getBuildingPostTransferNodes(buildingId: number, workflowId: number): Observable<BLEKeyDeliveryProgressApiResponse> {
    return this.networkMigrationResource.getBuildingPostTransferNodes(buildingId, workflowId);
  }

  getBuildingMigrationTransferProgress(buildingId: number, workflowId: number): Observable<BLEKeyDeliveryProgressApiResponse> {
    return this.networkMigrationResource
      .getBuildingMigrationTransferProgress(buildingId, workflowId)
      .pipe(map((response) => this.normalizeBLEKeyDeliveryResponse(response)));
  }

  private normalizeBLEKeyDeliveryResponse(
    response: BLEKeyDeliveryProgressApiResponse
  ): BLEKeyDeliveryProgressApiResponse {
    const candidateSteps = response.steps?.length ? response.steps : [];

    const steps = candidateSteps
      .filter((step) => step.step === 'BLE_KEY_DELIVERY')
      .map((step) => {
        const totalNodes = Number(step.totalNodes ?? 0);
        const completedNodes = Number(step.completedNodes ?? 0);
        const failedNodes = Number(step.failedNodes ?? 0);

        return {
          workflowStepId: step.workflowStepId ?? 0,
          step: step.step ?? 'BLE_KEY_DELIVERY',
          name: step.name ?? 'BLE key delivery',
          totalNodes,
          completedNodes,
          failedNodes,
          pendingNodes: Math.max(0, totalNodes - (completedNodes + failedNodes)),
          message: step.message || '',
          status: step.status,
          nodes: step.nodes || []
        };
      });

    return {
      steps,
      message: response.message,
      status: response.status,
      openJoiningSessionId: Number(response.openJoiningSessionId ?? 0),
      completedNodes: response.completedNodes,
      failedNodes: response.failedNodes,
      totalNodes: response.totalNodes,
      workflowId: response.workflowId,
      updatedAt: response.updatedAt
    };
  }
}